import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Sprout, MessageCircle } from "lucide-react";
import { openWhatsapp } from "@/lib/contact";

export default function Agro() {
    useEffect(() => {
        document.title = "Servicios Agro · Estudio Altez Palarin";
        window.scrollTo(0, 0);
    }, []);

    return (
        <main
            data-testid="agro-page"
            className="min-h-screen bg-[var(--uma-forest)] text-[var(--uma-cream)] relative overflow-hidden"
        >
            <div
                aria-hidden
                className="absolute -top-40 -right-40 w-[620px] h-[620px] rounded-full blur-3xl opacity-30"
                style={{
                    background:
                        "radial-gradient(circle, #C5A059, transparent 60%)",
                }}
            />
            <div
                aria-hidden
                className="absolute inset-0 opacity-[0.06] pointer-events-none"
                style={{
                    backgroundImage:
                        "repeating-linear-gradient(90deg, #C5A059 0 1px, transparent 1px 28px)",
                }}
            />

            <div className="relative max-w-5xl mx-auto px-5 md:px-10 py-12 md:py-16">
                <Link
                    to="/"
                    data-testid="agro-back"
                    className="inline-flex items-center gap-2 text-[13px] text-[var(--uma-cream)]/70 hover:text-[var(--uma-gold)] transition-colors"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Volver al estudio
                </Link>

                <div className="mt-14 md:mt-20 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--uma-gold)]/15 border border-[var(--uma-gold)]/30 text-[var(--uma-gold)] text-[11px] uppercase tracking-[0.22em]">
                        <Sprout className="h-3.5 w-3.5" />
                        Servicio especializado
                    </div>
                    <h1 className="font-serif-display text-[44px] md:text-[72px] lg:text-[88px] leading-[1] mt-8">
                        Servicios{" "}
                        <em className="italic font-light text-[var(--uma-gold)]">
                            Agro
                        </em>
                    </h1>
                    <p className="mt-7 max-w-2xl mx-auto text-[16px] md:text-[18px] leading-relaxed text-[var(--uma-cream)]/70 font-light">
                        Asesoramiento contable, fiscal y financiero dedicado al
                        sector agropecuario uruguayo. Productores, empresas
                        familiares, cooperativas y proyectos de inversión —
                        conocemos el campo porque trabajamos en el campo.
                    </p>

                    <div className="mt-10 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/5 border border-white/10 text-[13px] text-[var(--uma-cream)]/80">
                        <span className="w-1.5 h-1.5 rounded-full bg-[var(--uma-gold)] animate-pulse" />
                        Estamos terminando esta página — disponible muy pronto.
                    </div>

                    <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
                        <button
                            data-testid="agro-whatsapp"
                            onClick={() =>
                                openWhatsapp(
                                    "Hola Estela, vengo del sitio. Quiero más información sobre los servicios Agro.",
                                )
                            }
                            className="inline-flex items-center gap-2 bg-[var(--uma-gold)] text-[var(--uma-forest)] hover:bg-[var(--uma-cream)] px-7 py-4 rounded-full font-medium transition-all hover:-translate-y-0.5"
                        >
                            <MessageCircle className="h-4 w-4" />
                            Consultar por WhatsApp
                        </button>
                        <Link
                            to="/"
                            data-testid="agro-back-cta"
                            className="inline-flex items-center gap-2 border border-white/15 hover:bg-white/10 px-7 py-4 rounded-full font-medium transition-all"
                        >
                            Volver al inicio
                        </Link>
                    </div>
                </div>

                {/* Imagen agropecuaria */}
                <div className="mt-20 md:mt-24 relative rounded-3xl overflow-hidden border border-white/10 shadow-[0_30px_80px_-30px_rgba(10,38,20,0.5)]">
                    <img
                        src="https://images.unsplash.com/photo-1500595046743-cd271d694d30?auto=format&fit=crop&w=2000&q=70"
                        alt="Campo uruguayo — sector agropecuario"
                        className="w-full h-[280px] md:h-[420px] object-cover"
                        loading="lazy"
                    />
                    <div
                        aria-hidden
                        className="absolute inset-0 pointer-events-none"
                        style={{
                            background:
                                "linear-gradient(180deg, rgba(10,38,20,0.15) 0%, rgba(10,38,20,0.65) 100%)",
                        }}
                    />
                    <div className="absolute inset-x-0 bottom-0 p-6 md:p-10">
                        <div className="font-serif-display text-[24px] md:text-[36px] leading-tight text-[var(--uma-cream)] max-w-2xl">
                            Conocemos el campo porque{" "}
                            <em className="italic text-[var(--uma-gold)]">
                                trabajamos en el campo.
                            </em>
                        </div>
                        <p className="mt-3 text-[14px] md:text-[15px] text-[var(--uma-cream)]/75 max-w-xl font-light leading-relaxed">
                            Productores, empresas familiares y cooperativas
                            del interior uruguayo nos eligen por el
                            entendimiento real del rubro.
                        </p>
                    </div>
                </div>

                <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-3 gap-5">
                    {[
                        {
                            t: "Proyectos COMAP",
                            d: "Estructuramos y presentamos tu proyecto de inversión para acceder a beneficios fiscales del sector rural.",
                        },
                        {
                            t: "Créditos BROU",
                            d: "Acompañamiento integral para acceder a líneas de financiamiento del banco oficial.",
                        },
                        {
                            t: "IMEBA y obligaciones",
                            d: "Liquidaciones, DDJJ y todas las obligaciones específicas del productor agropecuario.",
                        },
                    ].map((c, i) => (
                        <div
                            key={i}
                            className="rounded-2xl border border-white/10 bg-white/[0.03] p-6"
                        >
                            <div className="font-serif-display text-[22px] text-[var(--uma-gold)] italic">
                                0{i + 1}
                            </div>
                            <h3 className="font-serif-display text-[22px] mt-3">
                                {c.t}
                            </h3>
                            <p className="mt-2 text-[14px] text-[var(--uma-cream)]/65 leading-relaxed font-light">
                                {c.d}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </main>
    );
}
